import { StringUtils } from '@manifoldxyz/js-ts-utils';
import { identityApi } from '@/api/identity';

type EnsResult = {
  resolved?: string | null;
};

const cacheTtl = 60 * 60 * 24 * 1000;
const cacheKeyPrefix = 'manifold/ens/';

const tryFromCache = (address: string): EnsResult | null => {
  if (!address) {
    return null;
  }

  try {
    const key = address.toLowerCase();
    const itemRaw = window.sessionStorage.getItem(`${cacheKeyPrefix}${key}`);
    if (itemRaw) {
      const item = JSON.parse(itemRaw);
      if (new Date(item.expiration) > new Date()) {
        return item;
      }
    }
  } catch (error) {
    console.warn('ENS cache lookup error:', error);
  }

  return null;
};

const cacheResult = (address: string, result: EnsResult): void => {
  const item = {
    ...result,
    expiration: new Date(Date.now() + cacheTtl),
  };

  window.sessionStorage.setItem(`${cacheKeyPrefix}${address.toLowerCase()}`, JSON.stringify(item));
};

const resolveENSFromBadges = async (address: string) => {
  const cachedENS = tryFromCache(address);
  if (cachedENS) {
    return cachedENS.resolved;
  }
  try {
    const userData = await identityApi.getUserData(address);
    if (!userData) {
      return null;
    }

    const userDataJson = await userData.json();

    if (!userDataJson.userENS) {
      return StringUtils.abbreviateAddress(address);
    }

    cacheResult(address, { resolved: userDataJson.userENS });
    return userDataJson.userENS;
  } catch {
    return null;
  }
};

const resolveAddressFromBadges = async (ens: string) => {
  const cachedAddress = tryFromCache(ens);
  if (cachedAddress) {
    return cachedAddress.resolved;
  }
  try {
    const userData = await identityApi.getUserData(ens);

    if (!userData) {
      return null;
    }

    const userDataJson = await userData.json();

    if (userDataJson.walletAddress) {
      cacheResult(ens, { resolved: userDataJson.walletAddress });
      return userDataJson.walletAddress;
    }
  } catch {
    return null;
  }
};

export { resolveENSFromBadges, resolveAddressFromBadges };
